var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Connection_1;
import { Utils } from '@symon-ai/wisepipe-common';
import { JSON, JsonDiscriminatorProperty, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { connectionSchemaValidation } from '../validations.generated';
export var ConnectionType;
(function (ConnectionType) {
    ConnectionType["Google"] = "google";
    ConnectionType["GSheetsImport"] = "gsheetsImport";
    ConnectionType["GSheetsExport"] = "gsheetsExport";
    ConnectionType["Shopify"] = "shopify";
    ConnectionType["SalesForce"] = "salesForce";
    ConnectionType["PostgreSql"] = "postgreSql";
    ConnectionType["Icm"] = "icm";
    ConnectionType["TqPostgres"] = "tq-postgres";
    ConnectionType["MySql"] = "mySql";
    ConnectionType["MsSql"] = "msSql";
    ConnectionType["Oracle"] = "oracle";
    ConnectionType["AmazonRedshift"] = "amazonRedshift";
    ConnectionType["AmazonAurora"] = "amazonAurora";
    ConnectionType["AmazonS3"] = "amazonS3";
    ConnectionType["AmazonS3Export"] = "amazonS3Export";
    ConnectionType["Tqp"] = "tqp";
    ConnectionType["Hubspot"] = "hubspot";
    ConnectionType["Symon"] = "symon";
    ConnectionType["Snowflake"] = "snowflake";
    ConnectionType["Dynamics"] = "dynamics";
    ConnectionType["AgentSync"] = "agentSync";
    ConnectionType["Workday"] = "workday";
    ConnectionType["WorkdayRaaS"] = "workdayRaaS";
    ConnectionType["WorkdayExport"] = "workdayExport";
    ConnectionType["Tableau"] = "tableau";
    ConnectionType["Sftp"] = "sftp";
    ConnectionType["PowerBI"] = "powerbi";
    ConnectionType["Rest"] = "rest";
    ConnectionType["Gong"] = "gong";
    ConnectionType["NetSuite"] = "netSuite";
    ConnectionType["OneDrive"] = "oneDrive";
    ConnectionType["OneDriveExport"] = "oneDriveExport";
    // REST based placeholder connections
    ConnectionType["SapSuccessFactors"] = "sapSuccessFactors";
    ConnectionType["SapCrm"] = "sapCrm";
    ConnectionType["Ukg"] = "ukg";
    ConnectionType["OracleHcm"] = "oracleHcm";
    ConnectionType["Adp"] = "Adp";
    ConnectionType["Dynamics365Sales"] = "dynamics365Sales";
    ConnectionType["CeridianDayforce"] = "ceridianDayforce";
    ConnectionType["OracleCrm"] = "oracleCrm";
    ConnectionType["OracleErpCloud"] = "oracleErpCloud";
    ConnectionType["SageIntacct"] = "sageIntacct";
    ConnectionType["BambooHr"] = "bambooHr";
    ConnectionType["SapErp"] = "sapErp";
    ConnectionType["GoogleCloudPlatformCloudStorage"] = "googleCloudPlatformCloudStorage";
    ConnectionType["Sas"] = "sas";
    ConnectionType["Dropbox"] = "dropbox";
    ConnectionType["GoogleDrive"] = "googleDrive";
    ConnectionType["Jira"] = "jira";
    ConnectionType["Monday"] = "monday";
    ConnectionType["Zuora"] = "zuora";
    ConnectionType["AdobeAnalytics"] = "adobeAnalytics";
    ConnectionType["ZoomInfo"] = "zoomInfo";
    ConnectionType["Marketo"] = "marketo";
    ConnectionType["ActiveCampaign"] = "activeCampaign";
    ConnectionType["Mailchimp"] = "mailchimp";
    ConnectionType["Infor"] = "infor";
    ConnectionType["CornerstoneOnDemand"] = "cornerstoneOnDemand";
    ConnectionType["Gusto"] = "gusto";
    ConnectionType["PaychexFlex"] = "paychexFlex";
    ConnectionType["Zendesk"] = "zendesk";
    ConnectionType["ServiceNow"] = "serviceNow";
    ConnectionType["ZohoCrm"] = "zohoCrm";
    ConnectionType["Pipedrive"] = "pipedrive";
    ConnectionType["QuickBooks"] = "quickBooks";
    ConnectionType["Xero"] = "xero";
    ConnectionType["Freshbooks"] = "freshbooks";
    ConnectionType["ZohoBooks"] = "zohoBooks";
    ConnectionType["WaveFinancial"] = "waveFinancial";
})(ConnectionType || (ConnectionType = {}));
export var OAuthAccessScopeType;
(function (OAuthAccessScopeType) {
    OAuthAccessScopeType["Import"] = "import";
    OAuthAccessScopeType["Export"] = "export";
})(OAuthAccessScopeType || (OAuthAccessScopeType = {}));
export const OAuthConnectionTypes = new Set([
    // Connectors that implement oauth_client_code (OAuth2) authentication.
    ConnectionType.Google,
    ConnectionType.GSheetsImport,
    ConnectionType.GSheetsExport,
    ConnectionType.Shopify,
    ConnectionType.SalesForce,
    ConnectionType.Hubspot,
    ConnectionType.Dynamics,
    ConnectionType.PowerBI,
    ConnectionType.Rest, // REST Connectors also look for RestAuthCredentialsType.OAuthClientCode because multiple authentication types are supported, and some REST connectors are configured for oauth_client_code (OAuth2).
    ConnectionType.OneDrive,
    ConnectionType.OneDriveExport,
    ConnectionType.Snowflake // Snowflake supports two authentication methods: OAuth, username/password
]);
// connectionID not in required for v1 backwards compatibility
export const connectionSchema = {
    $id: 'urn:wisepipe-common-connection:Connection',
    type: 'object',
    properties: {
        connectionID: { type: 'string', uid: 9 },
        connectionType: { type: 'string', enum: Object.values(ConnectionType) },
        name: { type: 'string', minLength: 1 },
        source: { type: 'string', minLength: 1 },
        failedOnLastTry: { type: 'boolean' },
        isDiscoveryFail: { type: 'boolean' },
        verificationErrorMessage: { type: 'string' }
    },
    required: ['connectionType', 'name']
};
// A `Connection` represents the most basic concept of a connection to a third party service.
// There should be ABSOLUTELY NO SECURE INFORMATION such as access tokens stored in this object,
// as this object will be sent over the web.
let Connection = Connection_1 = class Connection {
    constructor(connectionID, connectionType, name, source, failedOnLastTry, isDiscoveryFail, verificationErrorMessage) {
        this.connectionID = connectionID;
        this.connectionType = connectionType;
        this.name = name;
        this.source = source;
        this.failedOnLastTry = failedOnLastTry;
        this.isDiscoveryFail = isDiscoveryFail;
        this.verificationErrorMessage = verificationErrorMessage;
        JSON.validate(this, Connection_1);
    }
    static validateConnectionID(connectionID) {
        class ID extends Connection_1 {
            constructor(connectionID) {
                super(connectionID, ConnectionType.Icm, 'v');
            }
        }
        new ID(connectionID);
    }
    static newID() {
        return Utils.newID();
    }
    static bufferID(base64ID) {
        return Utils.base64StringToBuffer(base64ID);
    }
};
Connection = Connection_1 = __decorate([
    JsonSchemaV2(connectionSchema, connectionSchemaValidation),
    JsonDiscriminatorProperty('connectionType')
], Connection);
export { Connection };
//# sourceMappingURL=connection.js.map